<template>
    <div class="">
        <div class="relative custom_blocks">
            <!-- container -->
            <div class="pb-20 ">  
                <div class="flex flex-col items-center justify-between gap-6 md:flex-row md:gap-20 inside_padding ">
                <div class="md:w-1/2"> 
                    <h2 class="pt-2 text-4xl md:text-5xl lg:text-6xl custom_title font-pp">{{ $t('hero.title') }}</h2>
                </div>
                <div class="md:w-1/2"> 
                    <p class="md:text-xl font-gt-light 2xl:text-3xl">{{ $t('hero.subtitle') }}</p>
                    <div class="flex items-center gap-4 mt-8">
                        <CtaBtn :title="$t('common.letsWorkTogether')" :route="'/contact/'"/>
                    </div>
                </div>
                </div>
                

                <!-- video  -->
                <div class="sm:border mx-[.25rem] md:mx-10 custom_border p-2 h-[220px] xs:h-[220px] md:h-[35vh] lg:h-[80vh]  relative z-[2] mt-24">
                    <div class="relative size-full">
                        <!-- Poster (first video frame) is the LCP element: in the server HTML, high priority. -->
                        <picture>
                            <source type="image/avif" :srcset="posterSrcset(posterAvif)" :sizes="posterSizes">
                            <source type="image/webp" :srcset="posterSrcset(posterWebp)" :sizes="posterSizes">
                            <img
                                :src="posterWebp[720]"
                                alt=""
                                width="1280"
                                height="720"
                                fetchpriority="high"
                                class="absolute inset-0 object-cover size-full"
                            >
                        </picture>

                        <!-- The video is fetched only after the page has loaded, and fades in over the
                             poster once it plays. Its single source is chosen in JS (see startVideo). -->
                        <video
                            ref="video"
                            loop
                            muted
                            playsinline
                            preload="none"
                            aria-hidden="true"
                            class="absolute inset-0 object-cover transition-opacity duration-500 size-full"
                            :class="isPlaying ? 'opacity-100' : 'opacity-0'"
                            @playing="isPlaying = true"
                        ></video>
                    </div>


                    <!-- corners  -->
                    <div class=" hidden sm:block absolute left-0 top-0  inset-0  z-[100] ">
                        <div class="relative size-full ">
                            <!-- left top  -->
                            <span class=" absolute border_corner_left_top left-[-12.5px] top-[-12.5px] border-t border-l custom_border size-[25px]"></span>
                            <!-- left bottom  -->
                            <span class=" absolute border_corner_left_bottom left-[-12.5px] bottom-[-12.5px] border-b border-l custom_border size-[25px]"></span>
                            <!-- right top  -->
                            <span class=" absolute border_corner_right_top right-[-12.5px] top-[-12.5px] border-t border-r custom_border size-[25px]"></span>
                            <!-- right bottom  -->
                            <span class=" absolute border_corner_right_bottom right-[-12.5px] bottom-[-12.5px] border-b border-r custom_border size-[25px]"></span>
                        </div>
                    </div>
                </div>

            </div>
            <!-- container  -->
        </div>
    </div>
</template>
<script setup>
import { onMounted, ref } from 'vue'
import heroVideo from '~/assets/videos/hero.mp4'
import heroVideoMobile from '~/assets/videos/hero-mobile.mp4'
import poster480Avif from '~/assets/images/hero/hero-poster-480.avif'
import poster720Avif from '~/assets/images/hero/hero-poster-720.avif'
import poster960Avif from '~/assets/images/hero/hero-poster-960.avif'
import poster1280Avif from '~/assets/images/hero/hero-poster-1280.avif'
import poster480Webp from '~/assets/images/hero/hero-poster-480.webp'
import poster720Webp from '~/assets/images/hero/hero-poster-720.webp'
import poster960Webp from '~/assets/images/hero/hero-poster-960.webp'
import poster1280Webp from '~/assets/images/hero/hero-poster-1280.webp'

const posterAvif = { 480: poster480Avif, 720: poster720Avif, 960: poster960Avif, 1280: poster1280Avif }
const posterWebp = { 480: poster480Webp, 720: poster720Webp, 960: poster960Webp, 1280: poster1280Webp }
const posterSrcset = (set) => Object.entries(set).map(([w, url]) => `${url} ${w}w`).join(', ')
// Rendered width of the image: page gutters (#app mx-2/6/8/10 + borders) + card margin, border and padding.
// A phone at 412px / DPR 1.75 needs ~650px and gets the 720w file; 1x phones get 480w.
const posterSizes = '(min-width: 1536px) calc(100vw - 180px), (min-width: 1024px) calc(100vw - 164px), (min-width: 768px) calc(100vw - 148px), calc(100vw - 44px)'

// The inlined CSS makes <head> long; preload the poster so it is discovered right away.
useHead({
    link: [{ rel: 'preload', as: 'image', type: 'image/avif', imagesrcset: posterSrcset(posterAvif), imagesizes: posterSizes, fetchpriority: 'high' }],
})

const video = ref(null)
const isPlaying = ref(false)

// One source, picked here: with two <source> children, inserting them started the browser's source
// selection once on its own and load() started it again, so phones downloaded both files.
// Phones get the 640px encode (156 KiB); the 720p file (779 KiB) is only requested on wider screens.
const startVideo = () => {
    // Keep the still poster for reduced motion or data saver.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData) return
    if (!video.value) return
    video.value.src = window.matchMedia('(max-width: 767px)').matches ? heroVideoMobile : heroVideo
    video.value.play().catch(() => {})
}

onMounted(() => {
    const whenIdle = () => (window.requestIdleCallback ? requestIdleCallback(startVideo, { timeout: 3000 }) : setTimeout(startVideo, 1000))
    if (document.readyState === 'complete') whenIdle()
    else window.addEventListener('load', whenIdle, { once: true })
})
</script>
